import { ImageSize, Layer } from "../types";

interface Option {
  label: string;
  value: string;
}

interface UpdateRequest {
  layer: Layer;
  size: ImageSize;
}

export class ImageConfigUI extends EventTarget {
  private _el: HTMLElement;
  private _undoBtn: HTMLButtonElement;

  constructor() {
    super();
    const div = document.createElement("div");
    div.className = "absolute text-xs left-0 bg-white/50 backdrop-blur-md p-2 rounded image-config-ui flex gap-4 items-center";
    div.appendChild(this.createScrollSpeedControl());
    div.appendChild(this.createSizeControl());
    div.appendChild(this.createSwapButton());
    this._undoBtn = this.createUndoButton();
    div.appendChild(this._undoBtn);
    div.appendChild(this.createCloseButton());
    this._el = div;
  }

  show(state: boolean) {
    if (state) {
      this._el.classList.remove("hidden");
    } else {
      this._el.classList.add("hidden");
    }
  }

  updateUI(request: UpdateRequest) {
    ["layer", "size"].forEach(setting => {
      const buttons = this._el.querySelectorAll(`[data-setting="${setting}"] button`);
      const activeValue = request[setting as keyof UpdateRequest];
      this.updateButtonGroup(buttons, activeValue!);
    });
  }

  updateButtonGroup(buttons: NodeListOf<Element>, activeValue: string) {
    buttons.forEach(button => {
      if (button.getAttribute("data-value") === activeValue) {
        button.classList.add("bg-black", "text-white");
        button.classList.remove("bg-gray-200");
      } else {
        button.classList.remove("bg-black", "text-white");
        button.classList.add("bg-gray-200");
      }
    });
  }

  createScrollSpeedControl(): HTMLElement {
    const container = this.createGroupContainer("layer");

    const speeds: Option[] = [
      { label: "Slow", value: "background" },
      { label: "Medium", value: "middle" },
      { label: "Fast", value: "foreground" },
    ];

    // Three buttons for slow, medium, fast
    speeds.forEach(({ label, value }) => {
      const button = this.createButton(label, "layer", value);
      container.appendChild(button);
    });

    return container;
  }

  createSizeControl(): HTMLElement {
    const container = this.createGroupContainer("size");

    // Three buttons for small, medium, large
    ["xs", "sm", "md", "lg", "xl"].forEach((size) => {
      const button = this.createButton(size.toUpperCase(), "size", size);
      container.appendChild(button);
    });

    return container;
  }

  showUndo(state: boolean): void {
    this._undoBtn.classList.toggle("hidden", !state);
  }

  createUndoButton(): HTMLButtonElement {
    const icon = `<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><polyline points="80 136 32 88 80 40" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><path d="M80,200h88a56,56,0,0,0,56-56h0a56,56,0,0,0-56-56H32" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>`;
    const btn = this.createButton(icon, "undoSwap", "");
    btn.classList.add("hidden");
    return btn;
  }

  createSwapButton(): HTMLButtonElement {
    const swapIcon = `<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><polyline points="168 96 216 96 216 48" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><path d="M216,96,187.72,67.72A88,88,0,0,0,64,67" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><polyline points="88 160 40 160 40 208" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><path d="M40,160l28.28,28.28A88,88,0,0,0,192,189" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>`
    return this.createButton(swapIcon, "swap", "");
  }

  createCloseButton(): HTMLButtonElement {
    const xIcon = `<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><line x1="200" y1="56" x2="56" y2="200" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="24"/><line x1="200" y1="200" x2="56" y2="56" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="24"/></svg>`;
    return this.createButton(xIcon, "remove", "", "destructive");
  }

  createGroupContainer(key: string): HTMLElement {
    const groupContainer = document.createElement("div");
    groupContainer.className = "flex gap-0.5";
    groupContainer.setAttribute("data-setting", key);

    return groupContainer;
  }

  createButton(label: string, key: string, value: string, variant?: "default" | "destructive"): HTMLButtonElement {
    const button = document.createElement("button");
    button.innerHTML = label;
    button.setAttribute("data-value", value);

    const className = variant === "destructive" ? "bg-red-400 text-white hover:bg-red-600" : "bg-gray-200 hover:bg-gray-300";
    button.className = `p-1 rounded ${className}`;

    button.addEventListener("click", () => {
      const event = new CustomEvent("updateSetting", { detail: { key, value } });
      this.dispatchEvent(event);
    });
    return button;
  }

  get el(): HTMLElement {
    return this._el;
  }
}
